import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { Crypto } from '../services/helpers/encryption-utils';
import { LocalStorageService } from '../services/helpers/local-storage.service';
import { TokenService } from '../services/helpers/token.service';
import { isPublicReportLocation, isPublicReportUrl } from '../services/helpers/public-report-route';

export const afterLoginGuard: CanActivateFn = (_route, state) => {
  // `inject()` is only valid synchronously at the top of the guard, so resolve everything up front
  // rather than mid-function (the previous code called `inject(Router)`/`inject(LocalStorageService)`
  // from inside branches).
  const _tokenService = inject(TokenService);
  const _authService = inject(AuthService);
  const _router = inject(Router);
  const _localStorage = inject(LocalStorageService);
  const { url } = state;

  /**
   * Public, PIN-gated report link (`/report-view/:slug`). These URLs are emailed to CUSTOMERS who
   * have no Remmi account, so this check runs FIRST and unconditionally — before any token read,
   * before `_intended` is written, and regardless of whether a (possibly stale) token exists.
   *
   * Previously this sat below the `_intended` write and was gated on `!token`, so a visitor whose
   * storage still held a token that merely LOOKED valid fell through to the permission/logout
   * branches. `state.url` alone was also not enough: it is empty during some redirect replays,
   * hence the `window.location` fallback.
   */
  if (isPublicReportUrl(url) || isPublicReportLocation(url)) {
    // Load whatever permissions we already have so a genuinely logged-in agent opening the link
    // keeps their toolbar, but never block or redirect on the result.
    try {
      _authService.loadPermissions();
    } catch {
      /* a public visitor has no permissions — irrelevant here */
    }
    return true;
  }

  // Reading the token can never throw now, but this guard runs on every navigation and a throw
  // here dead-ends the router with the global loader still on screen.
  let token = false;
  try {
    token = _tokenService.isUserloggedIn();
  } catch {
    token = false;
  }

  let [, listingId] = url.split('id=');

  // Case 1: If URL contains 'listings/listing-approval' and user is NOT logged in, redirect to listing-preview
  if (url?.includes('listings/listing-approval') && !token) {
    _router.navigateByUrl(`/listing-preview/${listingId}`);
    return false;
  }

  // Case 2: Store intended URL if user is not on login/home page
  if (url !== '/login' && url !== '/') {
    _localStorage.SetItem('_intended', Crypto.encrypt(state.url));
  }

  // Case 3: If user is not logged in, redirect to login page
  if (!token) {
    _authService.logOutWithoutToken();
    return false;
  }

  // Case 4: User is logged in, load permissions and allow access
  _authService.loadPermissions();
  return true;
};
